/**
 * Four screens, and no patient id anywhere in the paths.
 *
 * See AppShell: the patient is the session's subject. A portal route that took
 * an id would be a portal route somebody could edit.
 *
 * PORTAL_ROUTE_PATHS is derived from the same data the routes are built from,
 * so a test that walks "every screen" cannot silently walk none. The clinician
 * app learned that the hard way — its equivalent list was an array of
 * `undefined` and a length assertion still passed.
 */

import { createRootRoute, createRoute, createRouter, type AnyRoute } from '@tanstack/react-router';
import type { JSX } from 'react';

import { AppShell } from './AppShell.js';
import { MyHealth } from './routes/MyHealth.js';
import { MyResults } from './routes/MyResults.js';
import { WhoCanSee } from './routes/WhoCanSee.js';
import { WhoHasSeen } from './routes/WhoHasSeen.js';
import { readSession } from './lib/auth.js';

/** The signed-in patient's id, or '' when there is no session. */
const currentPatientId = (): string => {
  const reference = readSession()?.profileRef ?? '';
  return reference.startsWith('Patient/') ? reference.slice('Patient/'.length) : '';
};

/** Every screen, as data. The route tree and the tests read this one list. */
export const PORTAL_SCREENS = [
  { path: '/', label: 'My health', render: MyHealth },
  { path: '/results', label: 'My results', render: MyResults },
  { path: '/access', label: 'Who has seen my record', render: WhoHasSeen },
  { path: '/permissions', label: 'Who can see what', render: WhoCanSee },
] as const;

export const PORTAL_ROUTE_PATHS: readonly string[] = PORTAL_SCREENS.map((screen) => screen.path);

const rootRoute = createRootRoute({ component: AppShell });

const screenRoutes: AnyRoute[] = PORTAL_SCREENS.map((screen) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: screen.path,
    component: (): JSX.Element => {
      const Screen = screen.render;
      return <Screen patientId={currentPatientId()} />;
    },
  }),
);

export const router = createRouter({ routeTree: rootRoute.addChildren(screenRoutes) });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
